import { useState, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  addWeeks,
  format,
} from "date-fns";

export function useCalendarView(requests: any[]) {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 0 })
  );

  const handlePrevMonth = () => setCurrentMonth((d) => addMonths(d, -1));
  const handleNextMonth = () => setCurrentMonth((d) => addMonths(d, 1));

  const handlePrevWeek = () => setCurrentWeekStart((d) => addWeeks(d, -1));
  const handleNextWeek = () => setCurrentWeekStart((d) => addWeeks(d, 1));

  const jumpToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setCurrentWeekStart(startOfWeek(today, { weekStartsOn: 0 }));
  };

  const jumpToDate = (dateInput: string | Date) => {
    const date =
      dateInput instanceof Date
        ? dateInput
        : new Date(`${String(dateInput).slice(0, 10)}T00:00:00`);

    if (Number.isNaN(date.getTime())) return;

    setCurrentMonth(date);
    setCurrentWeekStart(startOfWeek(date, { weekStartsOn: 0 }));
  };

  const monthMatrix = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    const days = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [currentMonth]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  const dayEvents = useMemo(() => {
    const map = new Map<string, any[]>();
    (requests || []).forEach((r: any) => {
      if (!r.start_date || !r.end_date) return;
      const startStr = String(r.start_date).slice(0, 10);
      const endStr = String(r.end_date).slice(0, 10);
      if (!startStr || !endStr) return;
      const start = new Date(`${startStr}T00:00:00`);
      const end = new Date(`${endStr}T00:00:00`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = format(d, "yyyy-MM-dd");
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(r);
      }
    });
    return map;
  }, [requests]);

  // useEffect(() => {
  //   Array.from(dayEvents.entries()).map(([date, arr]) => ({
  //     date,
  //     ids: arr.map((x) => x.id),
  //     ranges: arr.map((x) => [
  //       String(x.start_date).slice(0, 10),
  //       String(x.end_date).slice(0, 10),
  //     ]),
  //   }));
  // }, [dayEvents]);

  return {
    currentMonth,
    currentWeekStart,
    monthMatrix,
    weekDays,
    dayEvents,
    handlePrevMonth,
    handleNextMonth,
    handlePrevWeek,
    handleNextWeek,
    jumpToToday,
    jumpToDate,
  };
}
